import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Field from './Field';
import FieldList from './FieldList';
import typeMap from './typeMap';
import ValidationErrors from '../components/ValidationErrors';
import Sortable from '../components/Sortable';
import SortableHandle from '../components/SortableHandle';
import {FormTemplate} from '../models/JsonTypes';

const translationPrefix = 'kilowhat-formulaire.forum.items-editor.';

export default class Items extends Field {
    whiteListConfigProperties() {
        return super.whiteListConfigProperties().concat([
            'fields',
            'min',
            'max',
        ]);
    }

    configure() {
        // We cannot use the validationErrorsForThisField() helper here because we need the tree with nested errors as well
        const validationErrors = this.validationErrors[this.getConfig('key')] || {};

        const list = new FieldList({
            configs: this.getConfig('fields'),
            onchange: () => {
                this.onchange();
            },
            disabled: this.disabled,
            validationErrors,
            depth: this.depth + 1,
            expertMode: this.expertMode,
        });

        return m('.FormulaireConfigurationSubFields', [
            m(ValidationErrors, {
                errors: validationErrors, // The .errors key will automatically be retrieved by ValidationErrors
            }),
            this.minMaxRules(),
            list.configureView(),
        ]);
    }

    fill(display = false) {
        const configs: FormTemplate = this.getConfig('fields');

        // If no submission exists in the data, we need to create a new array first
        // This is important because we must be able to edit the array that's in the submissions variable via reference
        // Additionally we don't use this.setSubmission() in order to not trigger an onchange() event
        if (!this.submission[this.getConfig('key')]) {
            this.submission[this.getConfig('key')] = {
                value: [],
            };
        }

        const submissions = this.getSubmission();

        if (submissions.length === 0) {
            // Insert as many empty rows as necessary for the minimum required
            for (let i = 0; i < this.getConfig('min', 0); i++) {
                submissions.push({});
            }
        }

        const validationErrors = this.validationErrors[this.getConfig('key')] || {};

        //TODO: show the errors for this field

        return m('table', {
            className: display ? 'FormulaireDataTable' : 'FormulaireItemsTable',
        }, [
            m('thead', m('tr', [
                display ? null : m('th'),
                configs.map(config => {
                    const field = new typeMap[config.type]({
                        config,
                    });

                    const title = field.getConfig('title');
                    const description = field.getConfig('description');

                    return m('th', [
                        m('label', [
                            title ? title : app.translator.trans('kilowhat-formulaire.forum.template-editor.missing-title'),
                            !display && field.getConfig('required', false) ? m('span.FormulaireRequired', ' *') : null,
                        ]),
                        !display && description ? m('p', description) : null,
                    ]);
                }),
                display ? null : m('th'),
            ])),
            m(Sortable, {
                onsort: (origin: number, destination: number) => {
                    submissions.splice(destination, 0, submissions.splice(origin, 1)[0]);
                    this.onchange();
                },
                disabled: this.isFillDisabled(),
                containerTag: 'tbody',
                placeholderTag: 'tr',
            }, submissions.map((submission: any, index: number) => {
                const validationErrorsForThisRow = validationErrors[index] || {};

                return m('tr', {
                    key: 'item' + index, // Just so Sortable can work
                }, [
                    display ? null : m(SortableHandle, {
                        elementTag: 'td',
                        className: 'FormulaireItemsHandle',
                        disabled: this.isFillDisabled(),
                    }),
                    configs.map(config => {
                        const field = new typeMap[config.type]({
                            config,
                            submission,
                            uploader: this.uploader,
                            onchange: () => {
                                this.onchange();
                            },
                            disabled: this.isFillDisabled(),
                            validationErrors: validationErrorsForThisRow,
                            depth: this.depth + 1,
                        });

                        return m('td', [
                            display ? field.display() : field.fill(),
                            m(ValidationErrors, {
                                errors: validationErrorsForThisRow[config.key || ''],
                            }),
                        ]);
                    }),
                    display ? null : m('td', Button.component({
                        onclick: () => {
                            submissions.splice(index, 1);
                            this.onchange();
                        },
                        className: 'Button Button--icon',
                        icon: 'fas fa-trash',
                        disabled: this.isFillDisabled(),
                        title: app.translator.trans(translationPrefix + 'remove'),
                    })),
                ]);
            })),
            display ? null : m('tbody', m('tr', [
                m('td'),
                m('td', {
                    colspan: configs.length,
                }, Button.component({
                    onclick: () => {
                        submissions.push({});
                        this.onchange();
                    },
                    className: 'Button Button--block',
                    disabled: this.isFillDisabled() || (this.getConfig('max', 0) && submissions.length >= this.getConfig('max', '')),
                }, app.translator.trans(translationPrefix + 'add'))),
                m('td'),
            ])),
        ]);
    }

    display() {
        return this.fill(true);
    }
}
